import ConfirmModal from '@/components/ConfirmModal';
import { Icons } from '@/components/icons';
import PriceDisplay from '@/components/PriceDisplay';
import {
  AccordionContent,
  AccordionItem,
  AccordionPrimitiveRoot,
  AccordionTrigger,
} from '@/components/primitives/Accordion';
import { Button } from '@/components/primitives/Button';
import { Typography } from '@/components/primitives/Typography';
import { getPurchaseFlowBookingById } from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import { undoSeatSelection } from '@/features/purchase/purchaseService';
import { useSeatSelection } from '@/hooks/useSeatSelection';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { LegDTO } from '@/types/dto/agent';
import {
  findBookingBookedTripFeesByFeeRefs,
  findBookingBookedTripReservationByReservationId,
} from '@/utils/booking';
import { getPassengerName } from '@/utils/passenger';
import { useState } from 'react';

interface ReservedSeatsProps {
  leg: LegDTO & {
    passengers: Array<{
      firstName: string;
      lastName: string;
      reservedPlace?: {
        coachNumber?: string;
        placeNumber?: string[];
      };
      placeIcon?: 'seat' | 'compartment' | 'bed';
      reservationId?: string;
    }>;
    id: string;
  };
}

export const ReservedSeats = ({ leg }: ReservedSeatsProps) => {
  const dispatch = useDispatch();
  const { handleReservationSelect } = useSeatSelection();
  const booking = useSelector(purchaseFlowBookingSelector);
  const [isDeletingFee, setIsDeletingFee] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<{
    bookingId: string;
    feeRef: string;
  } | null>(null);

  const handleDeleteSeat = async () => {
    if (selectedReservation) {
      setIsDeletingFee(true);
      await undoSeatSelection(
        selectedReservation.bookingId,
        selectedReservation.feeRef
      );
      await dispatch(
        getPurchaseFlowBookingById(selectedReservation.bookingId)
      ).unwrap();
      setIsDeletingFee(false);
      setShowDeleteConfirm(false);
      setSelectedReservation(null);
    }
  };

  const passengersWithReservationId = leg.passengers.filter(
    (
      passenger
    ): passenger is Omit<typeof passenger, 'reservationId'> & {
      reservationId: string;
    } => passenger.reservationId !== undefined
  );

  return (
    <AccordionPrimitiveRoot type="single" defaultValue="open" collapsible>
      <AccordionItem value="open" className="border-none">
        <AccordionTrigger
          className="my-4 flex items-center py-0 text-primary hover:no-underline"
          justifyBetween={false}
        >
          <Icons.seat className="h-5 w-5" />
          <Typography variant="body1-bold">
            <TransText i18nKey="reservedSeats" />
          </Typography>
        </AccordionTrigger>
        <AccordionContent className="pb-0 pl-7">
          {passengersWithReservationId.map((passenger, index) => {
            const matchingReservation =
              findBookingBookedTripReservationByReservationId(
                booking,
                passenger.reservationId
              );
            const seatChangeFees = findBookingBookedTripFeesByFeeRefs(
              booking,
              matchingReservation?.feeRefs
            );
            const IconComponent = Icons[passenger.placeIcon ?? 'seat'];

            return (
              <div
                key={index}
                className="flex w-full flex-col gap-1 laptop:max-w-[400px]"
              >
                <div className="flex items-center justify-between">
                  <Typography variant="body1" className="truncate">
                    {getPassengerName(passenger, index)}
                  </Typography>

                  <div className="flex items-center gap-2">
                    {passenger.reservedPlace && (
                      <div className="flex items-center gap-2">
                        <Typography
                          variant="body2"
                          className="flex items-center gap-3 text-primary"
                        >
                          {passenger.reservedPlace.coachNumber &&
                            leg.ptMode?.code !== 'BUS' && (
                              <span className="flex items-center gap-0.5">
                                <Icons.carriage className="h-4 w-4" />
                                {passenger.reservedPlace.coachNumber}
                              </span>
                            )}
                          <span className="flex items-center gap-0.5">
                            <IconComponent width={16} height={16} />
                            {passenger.reservedPlace.placeNumber?.join(', ')}
                          </span>
                        </Typography>
                      </div>
                    )}
                    <Button
                      variant="icon"
                      size="small"
                      data-testid="select-seat-button"
                      onClick={() =>
                        handleReservationSelect(passenger.reservationId)
                      }
                      disabled={!passenger.reservationId}
                    >
                      <Icons.chevronRight className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                {!!seatChangeFees?.length && (
                  <div className="ml-2 flex items-center">
                    <div className="flex items-center gap-1">
                      <Typography variant="body2" className="text-neutral">
                        <TransText i18nKey="changedSeat" />:
                      </Typography>
                      <Typography
                        variant="body2-bold"
                        className="flex text-primary"
                      >
                        {'+'}
                        <PriceDisplay
                          amount={seatChangeFees.reduce(
                            (acc, cur) => acc + (cur.price?.amount || 0),
                            0
                          )}
                        />
                      </Typography>
                    </div>
                    <Button
                      variant="icon"
                      size="small"
                      data-testid="undo-seat-change-button"
                      disabled={isDeletingFee}
                      onClick={() => {
                        if (booking?.id && matchingReservation?.feeRefs?.[0]) {
                          setSelectedReservation({
                            bookingId: booking.id,
                            feeRef: matchingReservation.feeRefs[0],
                          });
                          setShowDeleteConfirm(true);
                        }
                      }}
                    >
                      <Icons.xSmall className="h-4 w-4 text-error-text" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          {passengersWithReservationId.length > 0 && (
            <>
              <Button
                variant="secondary"
                onClick={() =>
                  handleReservationSelect(
                    passengersWithReservationId[0].reservationId
                  )
                }
                data-testid="select-seats-button"
                disabled={!passengersWithReservationId[0].reservationId}
                className="mt-3"
              >
                <Icons.seat className="h-5 w-5" />
                <TransText i18nKey="selectSeats" />
              </Button>

              <ConfirmModal
                open={showDeleteConfirm}
                setOpen={setShowDeleteConfirm}
                title={<TransText i18nKey="deleteSeatChangeTitle" />}
                description={
                  <TransText i18nKey="deleteSeatChangeDescription" />
                }
                confirmLabel="confirm"
                cancelLabel="cancel"
                onConfirm={handleDeleteSeat}
                isConfirmLoading={isDeletingFee}
                confirmVariant="destructive"
              />
            </>
          )}
        </AccordionContent>
      </AccordionItem>
    </AccordionPrimitiveRoot>
  );
};
